import { Label } from "@nosible/shared/components/ui/label";
import { useSession } from "@/account/lib/auth-client";
import { trpc } from "@/account/lib/trpc";
import { EditUserName } from "@/account/components/EditUserName";
import { AccountPageHeader } from "@/account/components/AccountPageHeader";

function AccountPreferenceSwitch({
  id,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      data-slot="switch"
      data-state={checked ? "checked" : "unchecked"}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
    >
      <span data-slot="switch-thumb" aria-hidden="true" />
    </button>
  );
}

export function Account() {
  const { data: session } = useSession();

  const {
    data: profile,
    isLoading: profileLoading,
    refetch,
  } = trpc.user.getProfile.useQuery(undefined, {
    enabled: !!session,
  });

  const updatePreferences = trpc.user.updateEmailPreferences.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  if (profileLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-chromium-mist">Failed to load account</div>
      </div>
    );
  }

  const handlePreferenceChange = (
    key: "researchUpdates" | "productUpdates" | "apiUsage",
    value: boolean
  ) => {
    updatePreferences.mutate({ [key]: value });
  };

  return (
    <div className="account-ui-page account-settings">
      <AccountPageHeader
        title="Account Settings"
        description="Manage your profile and communication preferences."
      />

      <section className="account-settings-section">
        <div className="account-section-heading">
          <span><b>01</b> PROFILE</span>
        </div>
        <div className="account-settings-panel account-profile-panel">
          <div className="account-profile-row">
            <EditUserName
              currentName={profile.user?.name || null}
              userId={profile.user?.id || ""}
              onUpdate={refetch}
            />
          </div>
          <div className="account-profile-row">
            <Label>EMAIL</Label>
            <p>{profile.user?.email || "—"}</p>
            <span className="account-profile-row__meta">GOOGLE SIGN-IN</span>
          </div>
        </div>
      </section>

      <section className="account-settings-section">
        <div className="account-section-heading">
          <span><b>02</b> EMAIL PREFERENCES</span>
        </div>
        <p className="account-section-intro">Choose which notifications and updates you want to receive via email.</p>
        <div className="account-settings-panel account-preferences-panel">
          <div className="account-preference-row">
            <div>
              <Label htmlFor="research-updates">Research updates</Label>
              <p>Receive emails about new research papers and blog posts.</p>
            </div>
            <div className="account-preference-row__control">
              <span>{profile.emailPreferences.researchUpdates ? "ON" : "OFF"}</span>
              <AccountPreferenceSwitch
                id="research-updates"
                checked={profile.emailPreferences.researchUpdates}
                onCheckedChange={(checked: boolean) => handlePreferenceChange("researchUpdates", checked)}
                disabled={updatePreferences.isPending}
              />
            </div>
          </div>
          <div className="account-preference-row">
            <div>
              <Label htmlFor="product-updates">Product updates</Label>
              <p>Receive emails about new features and product announcements.</p>
            </div>
            <div className="account-preference-row__control">
              <span>{profile.emailPreferences.productUpdates ? "ON" : "OFF"}</span>
              <AccountPreferenceSwitch
                id="product-updates"
                checked={profile.emailPreferences.productUpdates}
                onCheckedChange={(checked: boolean) => handlePreferenceChange("productUpdates", checked)}
                disabled={updatePreferences.isPending}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
